import { useEffect, useState } from "react";
import { Send, Mail, MessageSquare, ChevronDown, ChevronUp, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";


export default function SequencePreview({ opp }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});
  const [log, setLog] = useState([]);

  const load = async () => {
    if (!opp?.oid) return;
    setLoading(true);
    try {
      const [p, l] = await Promise.all([
        api.get(`/sequences/preview/${opp.oid}`),
        api.get(`/sequences/log/${opp.oid}`),
      ]);
      setPreview(p.data);
      setLog(l.data.log || []);
    } catch (e) {
      // No preview available, possibly identity not set
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [opp?.oid]);

  const fire = async (stepIndex) => {
    setBusy((b) => ({ ...b, [stepIndex]: true }));
    try {
      const r = await api.post(`/sequences/fire/${opp.oid}`, { step_index: stepIndex });
      toast.success("Email sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Send failed`);
    } finally {
      setBusy((b) => ({ ...b, [stepIndex]: false }));
    }
  };

  if (loading) return <div className="text-slate-500 text-sm">Loading sequence...</div>;
  if (!preview) {
    return (
      <div className="ae-card p-5 text-sm text-slate-400 flex items-start gap-2" data-testid="sequence-preview-empty">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          Set your outreach identity in <a href="/app/settings" className="text-emerald-400 hover:underline">Settings</a> to preview the sequence.
        </div>
      </div>
    );
  }

  return (
    <div className="ae-card p-5 space-y-4" data-testid="sequence-preview">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Automated Outreach</div>
          <h3 className="font-heading text-xl tracking-tight">{preview.sequence_name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            4 emails on Days 1, 5, 8, 12. Sends from <span className="text-slate-300 font-mono">{preview.sender.sender_email}</span>, signed <span className="text-slate-300">{preview.sender.full_name}</span>.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {preview.messages.map((m, i) => {
          const isOpen = expanded[i];
          const isSent = log.find((l) => l.step === i + 1 && l.channel === m.channel);
          const Icon = m.channel === "email" ? Mail : MessageSquare;
          return (
            <div key={i} className="border border-white/8 rounded-sm bg-white/2"
                 data-testid={`sequence-step-${i}`}>
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <button onClick={() => setExpanded({ ...expanded, [i]: !isOpen })}
                        className="flex items-center gap-2 flex-1 text-left">
                  <Icon className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 text-sm">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Day {m.day}, {m.channel}</span>
                      {isSent && (
                        <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-mono inline-flex items-center gap-0.5">
                          <CheckCircle2 className="h-2.5 w-2.5" /> Sent
                        </span>
                      )}
                    </div>
                    <div className="text-slate-200 truncate">{m.subject || m.body.slice(0, 80) + "..."}</div>
                  </div>
                  {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-slate-500" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-500" />}
                </button>
                <button
                  onClick={() => fire(i)}
                  disabled={busy[i] || isSent}
                  data-testid={`sequence-fire-${i}`}
                  className="px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 shrink-0"
                >
                  <Send className="h-2.5 w-2.5" />
                  {busy[i] ? "..." : isSent ? "Sent" : "Send"}
                </button>
              </div>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 border-t border-white/5 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed font-mono">
                  {m.body}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {log.length > 0 && (
        <div className="pt-3 border-t border-white/8 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Send log</div>
          {log.map((l) => (
            <div key={l.smid} className="text-xs text-slate-400 flex items-center justify-between font-mono">
              <span>{l.channel} step {l.step}: {l.subject || l.body?.slice(0, 50)}</span>
              <span className="text-slate-500">{fmtDateTime(l.sent_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
